import { useEffect, useId, useRef, useState } from "react";
import styles from "./UserMenu.module.css";

interface UserMenuProps {
  email: string;
  onLogout: () => void;
  /** `pie` en la barra lateral (se abre hacia arriba, con el correo); `cabecera` en el móvil (solo avatar, hacia abajo). */
  variante: "pie" | "cabecera";
}

function initialOf(email: string): string {
  return (Array.from(email.trim())[0] ?? "?").toLocaleUpperCase("es");
}

/** Menú de la persona: correo y «Cerrar sesión». El tema vive en Ajustes (la tienda sigue al sistema). */
export function UserMenu({ email, onLogout, variante }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    wrapRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }
    function onPointerDown(event: PointerEvent) {
      if (event.target instanceof Node && !wrapRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${variante === "cabecera" ? styles.triggerCabecera : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Cuenta de ${email}`}
        data-etiqueta="Cuenta"
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.avatar} aria-hidden="true">{initialOf(email)}</span>
        {variante === "pie" ? <span className={styles.email}>{email}</span> : null}
      </button>
      {open ? (
        <div id={menuId} role="menu" aria-label="Cuenta" className={`${styles.menu} ${variante === "pie" ? styles.up : styles.down}`}>
          <p className={styles.menuEmail} role="none">{email}</p>
          <button type="button" role="menuitem" className={styles.item} onClick={() => { setOpen(false); onLogout(); }}>Cerrar sesión</button>
        </div>
      ) : null}
    </div>
  );
}
